import { bound } from "@frui.ts/helpers";
import { Router, watchBusy } from "@frui.ts/screens";
import { Modules } from "../../../config";
import type ChargeType from "entities/chargeType";
import type ChargeValidity from "entities/chargeValidity";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import EnumService from "services/enumService";
import PriceListService from "services/priceListService";
import { buildPriceListChargeTypes } from "utils/priceListChargeTypes";
import ListViewModel from "../../listViewModel";
import ChargeTypeDetailViewModel from "./chargeTypeDetailViewModel";
import { computed } from "mobx";

const navigationName = "charge-types";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [ChargeTypeDetailViewModel],
})
export default class ChargeTypesViewModel extends ListViewModel<ChargeType, ChargeTypeDetailViewModel> {
  readonly modelName = "chargeType";

  @computed
  get allowDelete(): boolean {
    return false;
  }

  @bound
  allowItemDetail() {
    return this.securityService.allowTo("enums");
  }

  @computed
  get allowWrite() {
    return false;
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof ChargeTypeDetailViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected confirmationService: ConfirmationService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @bound
  @watchBusy
  async fetchData() {
    // The price lists are loaded asynchronously on application start, so on a hard page reload
    // they are not available yet when this screen is opened.
    await this.priceListService.startChargeTypePriceListsInitialization();
    return { items: buildPriceListChargeTypes(this.priceListService, this.enumService) };
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `chargeTypes.${code}`, size);
  }

  translateValidFor(validFor: ChargeValidity) {
    return this.localizationService.translateEnum("chargeValidity", validFor);
  }
}
